import { EVENT_STATUS } from './recurrenceConstants'

/**
 * Compare two event arrays and categorize events as existing, new, or removed
 * @param {Array} baselineEvents - Original events array
 * @param {Array} currentEvents - New events array
 * @returns {Object} Object with categorized events and counts
 */
export const categorizeEventChanges = (baselineEvents, currentEvents) => {
  const baselineEventDates = new Set(baselineEvents.map(event => event.serviceDate))
  const currentEventDates = new Set(currentEvents.map(event => event.serviceDate))

  // Events that exist in current configuration
  const eventsWithStatus = currentEvents.map(event => {
    const hadBaseline = baselineEventDates.has(event.serviceDate)
    return {
      ...event,
      _status: hadBaseline ? EVENT_STATUS.EXISTING : EVENT_STATUS.NEW,
    }
  })

  // Events that existed in baseline but not in current (removed)
  const removedEvents = baselineEvents
    .filter(event => !currentEventDates.has(event.serviceDate))
    .map(event => ({
      ...event,
      id: `removed-${event.serviceDate}`,
      _status: EVENT_STATUS.REMOVED,
    }))

  // Combine all events
  const allEvents = [...eventsWithStatus, ...removedEvents].sort((a, b) => new Date(a.serviceDate) - new Date(b.serviceDate))

  // Calculate counts
  const existingCount = allEvents.filter(event => event._status === EVENT_STATUS.EXISTING).length
  const newCount = allEvents.filter(event => event._status === EVENT_STATUS.NEW).length
  const removedCount = allEvents.filter(event => event._status === EVENT_STATUS.REMOVED).length

  return {
    allEvents,
    eventsWithStatus,
    removedEvents,
    existingCount,
    newCount,
    removedCount,
  }
}

/**
 * Filter events for a specific month
 * @param {Array} events - Array of events
 * @param {Date} selectedDate - Date to get month/year from
 * @returns {Array} Events filtered to the specified month
 */
export const filterEventsForMonth = (events, selectedDate) => {
  if (!events || events.length === 0) return []

  return events.filter(({ serviceDateObj }) => {
    return serviceDateObj.getMonth() === selectedDate.getMonth() && serviceDateObj.getFullYear() === selectedDate.getFullYear()
  })
}

/**
 * Group events by day of month
 * @param {Array} events - Array of events
 * @returns {Object} Object with dayOfMonth as keys and arrays of events as values
 */
export const groupEventsByDay = events => {
  if (!events || events.length === 0) return {}

  return events.reduce((acc, event) => {
    const { dayOfMonth } = event
    if (!acc[dayOfMonth]) acc[dayOfMonth] = []
    acc[dayOfMonth].push(event)
    return acc
  }, {})
}

/**
 * Generate event statuses by day for calendar display
 * @param {Array} events - Array of events with _status property
 * @returns {Object} Object with dayOfMonth as keys and status strings as values
 */
export const generateEventStatusesByDay = events => {
  const statusMap = {}

  events.forEach(event => {
    const dayOfMonth = event.dayOfMonth
    if (!statusMap[dayOfMonth]) {
      statusMap[dayOfMonth] = event._status
    } else {
      // If a day has multiple events with different statuses, prioritize: removed > existing > new
      const currentStatus = statusMap[dayOfMonth]
      const eventStatus = event._status
      if (eventStatus === EVENT_STATUS.REMOVED || (currentStatus === EVENT_STATUS.NEW && eventStatus === EVENT_STATUS.EXISTING)) {
        statusMap[dayOfMonth] = eventStatus
      }
    }
  })

  return statusMap
}
